import { Box, Flex, Text } from "@nulogy/components";
import ThinDivider from "components/common/ThinDivider";
import { addListeners } from "components/helpers/shiftKeyListeners";
import Tag from "components/pages/schedule/Tag";
import WorkOrderCardStatus from "components/pages/schedule/workOrderCards/WorkOrderCardStatus";
import Schedule from "domain/Schedule";
import WorkBlock from "domain/WorkBlock";
import React, { useEffect } from "react";
import { useTheme } from "styled-components";
import DateTime from "utils/DateTime";
import BlockTooltip from "./BlockTooltip";
import ExpectedUnits from "./ExpectedUnits";
import Break from "./images/break.svg?react";
import RemainingDuration from "./RemainingDuration";
import WorkOrderFlag from "./WorkOrderFlag";

const calculateRemainingDuration = (workBlock, updatedBlock, displaySnappedRemainingDuration) => {
  const isInProgressOrFuture = DateTime.isInTheFuture(updatedBlock.endsAt);

  const remainingDuration = Schedule.recalculateRemainingDuration({
    newWorkBlock: isInProgressOrFuture ? updatedBlock : workBlock,
    workBlock,
  });

  return displaySnappedRemainingDuration ? Schedule.snapRemainingDuration(remainingDuration) : remainingDuration;
};

const blockDescription = ({ workOrder, workBlockLayout }) => {
  const { description, itemDescription, externalId, customerName } = workOrder;

  switch (workBlockLayout) {
    case WorkBlock.CUSTOMER_LAYOUT:
      return customerName || "No customer";

    case WorkBlock.ITEM_CODE_LAYOUT:
    case WorkBlock.ITEM_DESCRIPTION_LAYOUT:
      return itemDescription || "No item description";

    case WorkBlock.WORK_ORDER_DESCRIPTION_LAYOUT:
      return description || "No work order description";

    case WorkBlock.WORK_ORDER_ID_LAYOUT:
      return externalId || "No work order ID";

    default:
      throw new Error(`Unknown work block layout: ${workBlockLayout}`);
  }
};

export default function WorkBlockTooltip({
  applicationContext,
  displaySnappedRemainingDuration,
  endDate,
  workBlock,
  gapFiller,
  line,
  showStatus,
  startDate,
}) {
  const [pressedKey, setPressedKey] = React.useState("None");
  const [newWorkBlock, setNewWorkBlock] = React.useState(workBlock);
  const [remainingDuration, setRemainingDuration] = React.useState(workBlock?.remainingDuration || 0);

  useEffect(() => {
    addListeners({
      pressedCallback: (key) => setPressedKey(key),
      releasedCallback: () => setPressedKey("None"),
    });
  }, []);

  const updateWorkBlock = () => {
    const updatedBlock = WorkBlock.updateBlockWithGapFiller({
      block: workBlock,
      endDate,
      gapFiller,
      line,
      startDate,
    });

    setRemainingDuration(calculateRemainingDuration(workBlock, updatedBlock, displaySnappedRemainingDuration));

    setNewWorkBlock(updatedBlock);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reason lost to history, but pressedKey should be correlated with update
  useEffect(() => {
    updateWorkBlock(workBlock, startDate, endDate);
  }, [pressedKey, startDate, endDate]);

  const {
    jobId,
    workOrder,
    workOrder: { code: workOrderCode, flag, itemCode },
  } = workBlock;
  const {
    customerSettings: { workBlockLayout },
  } = applicationContext;
  const theme = useTheme();

  const description = blockDescription({ workBlockLayout, workOrder });

  const jobIdMessage = `Job ${jobId}`;

  return (
    <div
      className="b-sch-event-tooltip spec-work-block-tooltip"
      style={{ fontSize: `${theme.fontSizes.smaller} !important` }}
    >
      <Flex justifyContent="space-between" lineHeight="smallTextBase" pb="half">
        <Box>
          <Text data-testid="work-order-code" fontSize="small" fontWeight="medium" lineHeight="smallTextCompressed">
            {workOrderCode}
          </Text>
        </Box>
        {showStatus && (
          <Box lineHeight="smallTextCompressed">
            <WorkOrderCardStatus inline workOrder={workOrder} />
          </Box>
        )}
      </Flex>
      <Text data-testid="item-code" fontSize="smaller" lineHeight="smallerText" style={{ overflowWrap: "break-word" }}>
        {itemCode}
      </Text>
      <Text
        data-testid="description"
        fontSize="smaller"
        lineHeight="smallerText"
        mb="x1"
        style={{ overflowWrap: "break-word" }}
      >
        {description}
      </Text>
      <ThinDivider />
      <BlockTooltip
        endDate={newWorkBlock.endsAt}
        overlappingPlannedBreaks={newWorkBlock.overlappingPlannedBreaks}
        startDate={newWorkBlock.startsAt}
        teardownTimeDuration={workBlock.teardownTimeDuration}
        teardownTimeLabel={workBlock.teardownTimeLabel}
      />

      <ExpectedUnits
        workBlock={newWorkBlock}
        workTimeEndsAt={newWorkBlock.workTimeEndsAt}
        workTimeStartsAt={newWorkBlock.workTimeStartsAt}
      />
      <RemainingDuration remainingDurationInSeconds={remainingDuration} />
      {flag || jobId || newWorkBlock.overlappingPlannedBreaks.length > 0 ? <ThinDivider /> : null}
      <Flex>
        <div>
          {jobId ? (
            <Tag data-testid="job-tag" tooltipTag>
              {jobIdMessage}
            </Tag>
          ) : null}
        </div>
        <WorkOrderFlag data-testid="flag" flag={flag} />
        {newWorkBlock.overlappingPlannedBreaks.length > 0 && <Break />}
      </Flex>
    </div>
  );
}
